import { type File } from '@kalos/kalos-rpc';
import { Button, Dialog, DialogContent, DialogTitle, Skeleton } from '@kalos/ui';
import { ImageIcon, Play } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';

import { useS3FileUrlQuery } from '../../../../../hooks/react-query/useS3';

/** A square thumbnail from the file's signed URL; HEIC and failed loads show a placeholder. */
export const PhotoThumbnail = ({ file, onOpen }: { file: File; onOpen: () => void }) => {
  const urlQuery = useS3FileUrlQuery({ filter: { fileName: file.name, bucket: file.bucket } });
  const [hasFailed, setHasFailed] = useState(false);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open photo ${file.name}`}
      className="bg-muted flex aspect-square w-full items-center justify-center overflow-hidden rounded-md"
    >
      {urlQuery.isPending ? (
        <Skeleton className="h-full w-full" />
      ) : urlQuery.data && !hasFailed ? (
        <img
          src={urlQuery.data}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
          onError={() => setHasFailed(true)}
        />
      ) : (
        <ImageIcon className="text-muted-foreground h-6 w-6" aria-hidden />
      )}
    </button>
  );
};

/** A square video tile: its first frame, with a play mark, from the file's signed URL. */
export const VideoThumbnail = ({ file, onOpen }: { file: File; onOpen: () => void }) => {
  const urlQuery = useS3FileUrlQuery({ filter: { fileName: file.name, bucket: file.bucket } });
  const [hasFailed, setHasFailed] = useState(false);

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Play video ${file.name}`}
      className="bg-muted relative flex aspect-square w-full items-center justify-center overflow-hidden rounded-md"
    >
      {urlQuery.isPending ? (
        <Skeleton className="h-full w-full" />
      ) : urlQuery.data && !hasFailed ? (
        <video
          // Just enough of the file for its first frame; it plays in the viewer.
          src={`${urlQuery.data}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          aria-hidden
          tabIndex={-1}
          className="pointer-events-none h-full w-full object-cover"
          onError={() => setHasFailed(true)}
        />
      ) : null}
      <span className="bg-background/80 text-foreground absolute flex h-10 w-10 items-center justify-center rounded-full">
        <Play className="h-5 w-5" aria-hidden />
      </span>
    </button>
  );
};

/**
 * One photo (or video, which plays inline) at full screen, with labelled Previous, Next and Close, and a swipe left or right to
 * move between photos. `caption` adds a line about the photo, such as who took it and when;
 * `action` adds one control beside it, such as Open note.
 */
export const PhotoViewer = ({
  photos,
  index,
  onIndexChange,
  onClose,
  caption,
  action,
}: {
  photos: File[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  caption?: (photo: File) => ReactNode;
  action?: (photo: File) => ReactNode;
}) => {
  const photo = photos[index];
  const swipeStartX = useRef<number | null>(null);
  const goTo = (nextIndex: number) => {
    if (nextIndex >= 0 && nextIndex < photos.length) onIndexChange(nextIndex);
  };
  const urlQuery = useS3FileUrlQuery({ filter: { fileName: photo.name, bucket: photo.bucket } });
  const [failedName, setFailedName] = useState<string | null>(null);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showClose={false}
        // Full screen: nothing to type, so it must not re-center itself for the keyboard.
        keepFocusVisibleAboveKeyboard={false}
        className="bg-foreground text-background left-0 top-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none border-0 p-0 sm:rounded-none"
      >
        <DialogTitle className="sr-only">
          Photo {index + 1} of {photos.length}
        </DialogTitle>
        <div className="flex items-center justify-between gap-2 p-2">
          <span className="px-2 text-sm tabular-nums">
            {index + 1} / {photos.length}
          </span>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        </div>
        <div
          className="flex min-h-0 flex-1 touch-pan-y items-center justify-center"
          onTouchStart={(event) => {
            swipeStartX.current = event.touches[0].clientX;
          }}
          onTouchEnd={(event) => {
            if (swipeStartX.current === null) return;
            const distance = event.changedTouches[0].clientX - swipeStartX.current;
            swipeStartX.current = null;
            // A deliberate swipe, not a tap or a scroll that drifted.
            if (Math.abs(distance) > 50) goTo(distance < 0 ? index + 1 : index - 1);
          }}
        >
          {urlQuery.isPending ? (
            <Skeleton className="h-1/2 w-full" />
          ) : urlQuery.data && failedName !== photo.name && photo.mimeType.includes('video') ? (
            <video
              key={photo.name}
              src={urlQuery.data}
              controls
              playsInline
              aria-label={photo.name}
              className="max-h-full max-w-full"
              onError={() => setFailedName(photo.name)}
            />
          ) : urlQuery.data && failedName !== photo.name ? (
            <img
              src={urlQuery.data}
              alt={photo.name}
              className="max-h-full max-w-full object-contain"
              onError={() => setFailedName(photo.name)}
            />
          ) : (
            <div className="flex flex-col items-center gap-3 p-4 text-center text-sm">
              This photo can&apos;t be shown here.
              {urlQuery.data && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => window.open(urlQuery.data, '_blank', 'noopener')}
                >
                  Open the file
                </Button>
              )}
            </div>
          )}
        </div>
        {(caption || action) && (
          <div className="flex items-center justify-between gap-3 px-4 py-2 text-sm">
            <span className="min-w-0 break-words">{caption?.(photo)}</span>
            {action?.(photo)}
          </div>
        )}
        {photos.length > 1 && (
          <div className="flex justify-between gap-2 p-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
            <Button variant="secondary" disabled={index === 0} onClick={() => goTo(index - 1)}>
              Previous
            </Button>
            <Button
              variant="secondary"
              disabled={index === photos.length - 1}
              onClick={() => goTo(index + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
